import { useEffect, useState, useRef } from 'react'
import { Modal, Table } from 'react-bootstrap'
import Select from 'react-select'
import { sortObjectsByKey } from '@common/utils/helperFunctions'
import { useAlertDispatch, useAlertState } from '@context/alert/context/alert.context'
import { useDeviceState } from '@context/device/context/device.context'
import DateRangeComponent from '../../main/Components/Calendar/DateRangeSelector'
import {
  Alert,
  AlertAPI,
  AlertGroup,
  AlertQuery,
  AlertStatus,
  HistoricalAlert,
  HistoricalAlertTier,
  Stats,
} from '@context/alert/model/alert.model'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import AlertQueueItem from './AlertQueueItem'
import api from '@api/backend'
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js'
import HistoricalStats from '../Components/HistoricalAlertsStats/HistoricalAlertsStats'
import { buildPDFReport } from './helper-functions'
import { useAccountState } from '@context/account/context/account.context'
import { DeviceIDTypes } from '@context/device/model/device'
import { format, min } from 'date-fns'
import { TZDate } from '@date-fns/tz'

ChartJS.register(ArcElement, Tooltip, Legend)

export default function HistoricalAlertView() {
  const { devices, loadingDevices } = useDeviceState()
  const { historical, isLoading } = useAlertState()
  const { selectedAccounts } = useAccountState()
  const { loadHistoricalAlerts } = useAlertDispatch()
  const [selectedHistoricalGroups, setSelectedHistoricalGroups] = useState<any[]>([])
  const [selectedHistoricalGroup, setSelectedHistoricalGroup] = useState<any>()
  const [selectedAlertGroup, setSelectedAlertGroup] = useState<AlertGroup>()
  const [sortingConf, setSortingConf] = useState({ key: '', order: '' })
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [filtered, setFiltered] = useState<HistoricalAlert[]>([])
  const [statsTotal, setStatsTotal] = useState<Stats>({
    tier1Total: 0,
    tier2Total: 0,
    tier3Total: 0,
    totalAll: 0,
  })

  const tableRef = useRef(null)
  const totalChartRef = useRef(null)
  const top5ChartRef = useRef(null)

  useEffect(() => {
    const sortedObjects = sortObjectsByKey(
      historical.filter((h) => {
        const device = devices.find((d) => d.dUUID === h.dUUID)
        return (
          device &&
          (searchTerm.length === 0 ||
            h.deviceId.includes(searchTerm) ||
            device.deviceName.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
        )
      }),
      sortingConf.key,
      sortingConf.order,
    )

    if (fromDate && toDate) {
      const dateFiltered = filterObjectsByDateRange(sortedObjects, fromDate, toDate)

      setFiltered(dateFiltered)
    } else {
      setFiltered(sortedObjects)
    }
  }, [devices, searchTerm, historical, sortingConf, fromDate, toDate])

  useEffect(() => {
    setStatsTotal(
      filtered
        .filter((h) => !!devices.find((d) => d.dUUID === h.dUUID))
        .reduce(
          (accumulator, historical: HistoricalAlert) => {
            return {
              tier1Total: accumulator.tier1Total + historical.tier1.length,
              tier2Total: accumulator.tier2Total + historical.tier2.length,
              tier3Total: accumulator.tier3Total + historical.tier3.length,
              totalAll:
                accumulator.totalAll +
                historical.tier1.length +
                historical.tier2.length +
                historical.tier3.length,
            }
          },
          { tier1Total: 0, tier2Total: 0, tier3Total: 0, totalAll: 0 } as Stats,
        ),
    )
  }, [devices, historical, filtered])

  useEffect(() => {
    if (!selectedHistoricalGroup) return
    const device = devices.find((d) => d.dUUID === selectedHistoricalGroup.dUUID)
    if (!device) return
    // console.log(selectedHistoricalGroup.group)
    api
      .get(
        `usage_alerts/${selectedHistoricalGroup.dUUID}/history/${selectedHistoricalGroup.group.gid}`,
        {
          params: {
            id_type: DeviceIDTypes.dUUID,
            uom: selectedHistoricalGroup.deviceUom,
          },
        },
      )
      .then((res: any) => res.data.data)
      .then((res: AlertAPI[]) => {
        const sentDates = res.map((d) => new Date(d.hourly_alerts_sent_date!))
        const minSentDate = format(min(sentDates), "yyyy-MM-dd'T'HH:mm:ss.SSSSSSSxx")
        const alerts: Alert[] = res.map((alert) => {
          return {
            id: alert.id,
            gid: alert.gid,
            time: alert.alert_time,
            utc_time: alert.alert_time_utc,
            priorityTier: alert.ack_result,
            alert_score: alert.alert_score,
            status: AlertStatus.CLOSED,
            triggerType: alert.trigger_type,
            estimatedLeak: alert.estimated_leak,
            createdAt: alert.created_at,
            hourlyAlertsSent: alert.hourly_alerts_sent,
            actioned: alert.actioned,
            valid: alert.valid
          }
        })
        setSelectedAlertGroup({
          deviceLocation: device.deviceName,
          deviceUom: device.deviceSettings.uom,
          priorityTier: selectedHistoricalGroup.priorityTier,
          status: AlertStatus.CLOSED,
          triggerType: res[0].trigger_type,
          id: String(res[0].id),
          gid: res[0].gid,
          hourlyAlertsSentDate: minSentDate,
          alertCloseDate: res[0].closed_date!,
          startAlertTimeUTC: res[alerts.length - 1].alert_time_utc,
          startAlertTime: res[alerts.length - 1].alert_time,
          endAlertTime: res[0].alert_time,
          endAlertTimeUTC: res[0].alert_time_utc,
          alerts,
          deviceId: device.deviceId,
          dlId: res[0].dl_id,
          dUUID: res[0].d_uuid,
          estimatedLeakTotal: 0,
        })
      })
  }, [devices, selectedHistoricalGroup])

  useEffect(() => {
    const query: AlertQuery = {
      ackResult: [1, 2, 3]
    }
    loadHistoricalAlerts(query)
  }, [])

  function onColumnHeaderClick(columnKey: string) {
    let order: string
    if (sortingConf.key === columnKey) {
      if (sortingConf.order === '') {
        order = 'DESC'
      } else if (sortingConf.order === 'DESC') {
        order = 'ASC'
      } else {
        order = ''
      }
    } else {
      order = 'DESC'
    }
    setSortingConf({ key: columnKey, order })
  }

  function getSortingClass(columnKey: string) {
    return sortingConf.key === columnKey && sortingConf.order !== ''
      ? `fas fa-arrow-${sortingConf.order === 'ASC' ? 'down' : 'up'}`
      : ''
  }

  function filterTiersByDateRange(tiers: HistoricalAlertTier[], start: Date, end: Date): HistoricalAlertTier[] {
    return tiers.filter((t: HistoricalAlertTier) => {
      const dateInTier = new Date(t.start_date)
      return dateInTier >= start && dateInTier <= end
    })
  }

  function filterObjectsByDateRange(alerts: HistoricalAlert[], start: Date, end: Date) {
    return alerts
      .map((obj) => ({
        ...obj,
        tier1: filterTiersByDateRange(obj.tier1, start, end),
        tier2: filterTiersByDateRange(obj.tier2, start, end),
        tier3: filterTiersByDateRange(obj.tier3, start, end),
      }))
      .filter((obj) => obj.tier1.length > 0 || obj.tier2.length > 0 || obj.tier3.length > 0)
  }

  const exportPageAsPdf = async () => {
    const tableHTML = tableRef.current
    if (!totalChartRef) return
    if (!totalChartRef.current) return
    if (!top5ChartRef) return
    if (!top5ChartRef.current) return
    if (!tableHTML) return

    const accountName = selectedAccounts[0].name

    const totalChartCanva = await html2canvas(totalChartRef.current)
    const totalChartImage = totalChartCanva.toDataURL('image/png')

    const top5ChartCanva = await html2canvas(top5ChartRef.current)
    const top5ChartImage = top5ChartCanva.toDataURL('image/png')

    buildPDFReport(accountName, fromDate, toDate, totalChartImage, top5ChartImage, tableHTML).then(
      (pdf: jsPDF) => {
        pdf.save(`${accountName}_Alert_History_${format(new Date(), 'dd-MM-yyyy_HH:mm')}`)
      },
    )
  }

  return isLoading || loadingDevices ? (
    <div className="spinner-border m-auto mb-5 mt-5 d-flex text-primary" />
  ) : (
    <>
      <h1 className="mt-4 mb-4">Alert History</h1>

      <div>
        {selectedAccounts.length === 1 && (
          <div className="d-flex justify-content-end">
            <button className="btn btn-primary mb-4" onClick={() => exportPageAsPdf()}>
              Export page as PDF
            </button>
          </div>
        )}

        <div style={{ width: '100%' }}>
          <div className="input-group mb-3">
            <div className="form-group search-bar">
              <span className="fa fa-search search-icon"></span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by device name or ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="fa fa-times clear-icon" onClick={() => setSearchTerm('')}></span>
            </div>
            <div className="input-group-append result-number">
              <span>( {filtered.length} )</span>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-12 mb-3 p-0">
          <DateRangeComponent
            from={fromDate}
            to={toDate}
            fromCallback={setFromDate}
            toCallback={setToDate}
            label={'Date Range'}
            isClearable
          />
        </div>
      </div>

      {historical.length > 0 ? (
        <div>
          <HistoricalStats
            historical={filtered}
            totalChartRef={totalChartRef}
            top5ChartRef={top5ChartRef}
          />
          <div>
            <Table ref={tableRef} responsive>
              <thead>
                <tr>
                  <th>Device Location</th>
                  <th
                    className="clickable"
                    style={{ width: '15%', textAlign: 'center' }}
                    onClick={() => onColumnHeaderClick('tier1')}
                  >
                    <i className={getSortingClass('tier1')}></i>Tier 1
                  </th>
                  <th
                    className="clickable"
                    style={{ width: '15%', textAlign: 'center' }}
                    onClick={() => onColumnHeaderClick('tier2')}
                  >
                    <i className={getSortingClass('tier2')}></i>Tier 2
                  </th>
                  <th
                    className="clickable"
                    style={{ width: '15%', textAlign: 'center' }}
                    onClick={() => onColumnHeaderClick('tier3')}
                  >
                    <i className={getSortingClass('tier3')}></i>Tier 3
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered
                  .filter((h) => !!devices.find((d) => d.dUUID === h.dUUID))
                  .map((h) => {
                    const device = devices.find((d) => d.dUUID === h.dUUID)!
                    return (
                      <tr key={h.deviceId}>
                        <td>
                          {device.deviceName} (#{h.deviceId})
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-link te text-decoration-none px-1"
                            onClick={() => {
                              setSelectedHistoricalGroups(
                                h.tier1.map((g) => {
                                  return {
                                    deviceId: h.deviceId,
                                    dlId: h.dlId,
                                    dUUID: h.dUUID,
                                    deviceUom: device.deviceSettings.uom,
                                    timezone: device.deviceSettings.timezone,
                                    deviceLocation: device.deviceName,
                                    priorityTier: 1,
                                    group: g,
                                  }
                                }),
                              )
                            }}
                          >
                            {h.tier1.length}
                          </button>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-link te text-decoration-none px-1"
                            onClick={() => {
                              setSelectedHistoricalGroups(
                                h.tier2.map((g) => {
                                  return {
                                    deviceId: h.deviceId,
                                    dlId: h.dlId,
                                    dUUID: h.dUUID,
                                    deviceUom: device.deviceSettings.uom,
                                    timezone: device.deviceSettings.timezone,
                                    deviceLocation: device.deviceName,
                                    priorityTier: 2,
                                    group: g,
                                  }
                                }),
                              )
                            }}
                          >
                            {h.tier2.length}
                          </button>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-link te text-decoration-none px-1"
                            onClick={() => {
                              setSelectedHistoricalGroups(
                                h.tier3.map((g) => {
                                  return {
                                    deviceId: h.deviceId,
                                    dlId: h.dlId,
                                    dUUID: h.dUUID,
                                    deviceUom: device.deviceSettings.uom,
                                    timezone: device.deviceSettings.timezone,
                                    deviceLocation: device.deviceName,
                                    priorityTier: 3,
                                    group: g,
                                  }
                                }),
                              )
                            }}
                            // disabled={h.tier3.length === 0}
                          >
                            {h.tier3.length}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <b>Total Alerts ({statsTotal.totalAll})</b>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <b>{statsTotal.tier1Total}</b>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <b>{statsTotal.tier2Total}</b>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <b>{statsTotal.tier3Total}</b>
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>
      ) : (
        <div>
          <h5>No Historical Alerts For Selected Accounts</h5>
        </div>
      )}
      <Modal
        show={selectedHistoricalGroups && selectedHistoricalGroups.length > 0}
        onHide={() => {
          setSelectedAlertGroup(undefined)
          setSelectedHistoricalGroups([])
          setSelectedHistoricalGroup(undefined)
        }}
        backdrop="static"
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header className="modal-header" closeButton>
          <Modal.Title id="contained-modal-title-vcenter">{selectedHistoricalGroups && selectedHistoricalGroups.length ?  `Tier ${selectedHistoricalGroups[0].priorityTier} Alerts - ${selectedHistoricalGroups[0].deviceLocation}` : null}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedHistoricalGroups && selectedHistoricalGroups.length ? (
            <div>
              <Select
                value={selectedHistoricalGroup}
                onChange={(group: any) => setSelectedHistoricalGroup(group)}
                options={selectedHistoricalGroups}
                classNamePrefix="select"
                getOptionValue={(option: any) => option.group}
                getOptionLabel={(option: any) =>
                  `${format(TZDate.tz(option.group.timezone, option.group.start_date), 'EEE, dd/MM/yy h:mm a')} - ${format(TZDate.tz(option.group.timezone, option.group.end_date), 'EEE, dd/MM/yy h:mm a')}`
                }
                isClearable={true}
              />
              <div className="mt-1">
                <AlertQueueItem alertGroupItem={selectedAlertGroup} historical={true} />
              </div>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  )
}
